import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { Autocomplete, ConfirmationModal, RadioButtonWithLabel, Spinner, Tooltip, Typography, useOverlayContext, getOptionLabel, getOptionSelected } from 'seagull';
import { useStoreActions } from "../../contexts";
import { useRelationship } from "../../hooks/reactQuery/queries/useRelationship";
import { useFetchContactAgreements } from "../../hooks/useGetAgreements";
import { handlePromise, handleRequest } from "../../infra/utils";
import { getInvoiceDateInfo } from "../../pages/AgreementMinisite/BillingHub/BillingHub.utils";
import { genericConnector } from "../../utils/react-hook-form.utils";
import { getGlobalBillServiceOptions } from "../../business-logic/billServiceSelector";
import { createEmptyChargeItem, createEmptyServiceItem } from "../BillClientModal/BillClientItem.utils";
import ContactsSelector from "./ContactsSelector/ContactsSelector";
import styles from "./GlobalBillModal.module.scss";
import { BillingOption, GlobalBillModalErrorMessages } from "./GlobalBillModal.types";
import { getServiceOptionSelected, getServiceOptionLabel } from "./GlobalBillModal.utils";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export default function GlobalBillModal(_ref) {
  let {
    dismissModal,
    handleOpenBillClient
  } = _ref;
  const {
    showErrorToast
  } = useOverlayContext();
  const {
    api
  } = useStoreActions();
  const {
    fetchAgreements,
    agreements,
    isOnlyOneAgreement,
    loading
  } = useFetchContactAgreements();
  const [selectedCheckboxOption, setSelectedCheckboxOption] = useState(BillingOption.outOfScope);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [globalBillCommonData, setGlobalBillCommonData] = useState(null);
  const [isCommonDataLoading, setIsCommonDataLoading] = useState(false);
  const overlayContext = useOverlayContext();
  const {
    control,
    watch,
    setValue,
    handleSubmit
  } = useForm({
    mode: 'onSubmit'
  });
  const [selectedService, selectedAgreement, selectedContact] = watch(['selectedService', 'selectedAgreement', 'selectedContactId']);
  const {
    refresh
  } = useRelationship((selectedAgreement === null || selectedAgreement === void 0 ? void 0 : selectedAgreement.value) || '');
  const showServicesSelector = selectedCheckboxOption === BillingOption.inAgreement && selectedAgreement && selectedContact;
  useEffect(() => {
    if (!selectedAgreement || !selectedAgreement.value) return;
    const selectedAgreementId = selectedAgreement.value;
    setIsCommonDataLoading(true);
    Promise.all([handlePromise(refresh()), handleRequest(api.relationship.getBillingControlData(selectedAgreementId)), handleRequest(api.invoiceDraft.getDates(selectedAgreementId))]).then(_ref2 => {
      let [relationship, controlData, dates] = _ref2;
      if (!relationship.success || !relationship.data || !controlData.success || !dates.success) {
        showErrorToast(GlobalBillModalErrorMessages.FailedToRequestServices, 'global-bill-failed-to-fetch');
        return;
      }
      setGlobalBillCommonData({
        relationship: relationship.data,
        controlData: controlData.data,
        dates: dates.data
      });
      setServiceOptions(getGlobalBillServiceOptions({
        services: relationship.data.services,
        controlData: controlData.data
      }));
      setIsCommonDataLoading(false);
    });
  }, [selectedAgreement]);
  const onClearAgreement = () => {
    setSelectedCheckboxOption(BillingOption.outOfScope);
    setValue('selectedService', null, {
      shouldDirty: false
    });
    setServiceOptions([]);
  };
  const handleChangeContact = contactId => {
    onClearAgreement();
    setValue('selectedAgreement', null);
    if (contactId) {
      fetchAgreements(contactId).then(agreements => {
        if ((agreements === null || agreements === void 0 ? void 0 : agreements.length) === 1) {
          setValue('selectedAgreement', agreements[0]);
        }
      });
    }
  };
  const onOpenAddCharge = async globalBillCommonData => {
    const {
      relationship,
      controlData,
      dates
    } = globalBillCommonData;
    const selectedDate = dates.dates().length > 0 ? dates.dates()[0] : null;
    const invoiceDateInfo = getInvoiceDateInfo(selectedDate, dates);
    const context = {
      relationshipId: relationship.id,
      totalCredits: controlData.totalCredits,
      invoiceDateInfo,
      earliestBillableDate: dates.earliestBillableDate(),
      manualServicesBilling: controlData.manualServices,
      controlData,
      defaultItem: createEmptyChargeItem()
    };
    handleOpenBillClient(context);
  };
  const onOpenBillService = async (globalBillCommonData, service) => {
    const {
      relationship,
      controlData,
      dates
    } = globalBillCommonData;
    const selectedDate = dates.dates().length > 0 ? dates.dates()[0] : null;
    const invoiceDateInfo = getInvoiceDateInfo(selectedDate, dates);
    const billServiceData = {
      relationshipId: relationship.id,
      defaultItem: createEmptyServiceItem(service),
      totalCredits: controlData.totalCredits,
      manualServicesBilling: controlData.manualServices,
      invoiceDateInfo,
      earliestBillableDate: dates.earliestBillableDate(),
      controlData
    };
    handleOpenBillClient(billServiceData);
  };
  const handleGlobalBillModalSubmit = () => {
    const selectedServiceId = selectedService === null || selectedService === void 0 ? void 0 : selectedService.id;
    const service = globalBillCommonData === null || globalBillCommonData === void 0 ? void 0 : globalBillCommonData.relationship.services.find(s => s.id === selectedServiceId);
    if (agreements.length === 0) {
      showErrorToast('This client currently has no agreement with you.', 'global-bill-modal-no-agreements-error-toast');
      return;
    }
    if (selectedCheckboxOption === BillingOption.outOfScope && globalBillCommonData) {
      onOpenAddCharge(globalBillCommonData);
      dismissModal();
    } else if (selectedCheckboxOption === BillingOption.inAgreement && globalBillCommonData && service) {
      onOpenBillService(globalBillCommonData, service);
      dismissModal();
    } else {
      overlayContext.showErrorToast('Failed to open bill service modal', 'open-bill-service-modal-error');
    }
  };
  const handleChooseBillingOption = value => {
    setValue('selectedService', null);
    setSelectedCheckboxOption(value);
  };
  function renderAgreementsField() {
    if (loading) {
      return /*#__PURE__*/_jsx(Spinner, {
        size: 32,
        centerBoxHeight: 36,
        centeredInBox: true
      });
    }
    return agreements.length > 1 ? /*#__PURE__*/_jsx(Controller, {
      control: control,
      name: "selectedAgreement",
      rules: requiredValidation(true),
      render: renderProps => /*#__PURE__*/_jsx(Autocomplete, {
        selectOnFocus: true,
        fullWidth: true,
        "data-testid": "global-bill-modal-agreements-selector",
        textFieldProps: {
          label: 'Agreement',
          error: genericConnector(renderProps).showErrors
        },
        value: renderProps.field.value,
        options: agreements,
        getOptionLabel: getOptionLabel,
        getOptionSelected: getOptionSelected,
        onChange: (event, value) => {
          const newOption = value;
          onClearAgreement();
          if (newOption !== selectedAgreement) {
            renderProps.field.onChange(newOption);
          }
        }
      })
    }) : isOnlyOneAgreement ? null : /*#__PURE__*/_jsx(Typography, {
      "data-testid": "global-bill-modal-no-agreements",
      className: styles.noAgreementsText,
      variant: "paragraph1",
      skin: "secondary",
      children: "This client currently has no agreement with you."
    });
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(ConfirmationModal, {
      title: "Bill client",
      "data-testid": "global-bill-modal",
      primaryButtonTestid: "global-bill-modal-continue-button",
      textAlignCenter: false,
      onCloseButtonClick: dismissModal,
      primaryButtonLabel: "Continue",
      secondaryButtonLabel: "Cancel",
      onSecondaryButtonClick: dismissModal,
      primaryButtonLoading: isCommonDataLoading,
      onPrimaryButtonClick: handleSubmit(handleGlobalBillModalSubmit),
      content: /*#__PURE__*/_jsxs("div", {
        className: styles.globalBillModalContent,
        children: [/*#__PURE__*/_jsx(Controller, {
          control: control,
          name: "selectedContactId",
          rules: requiredValidation(true),
          render: renderProps => /*#__PURE__*/_jsx(ContactsSelector, {
            disabled: loading,
            selectedContactId: renderProps.field.value,
            error: genericConnector(renderProps).showErrors,
            onChange: value => {
              renderProps.field.onChange(value);
              handleChangeContact(value);
            }
          })
        }), selectedContact && renderAgreementsField(), /*#__PURE__*/_jsxs("div", {
          children: [selectedAgreement && /*#__PURE__*/_jsxs("div", {
            "data-testid": "global-bill-radio-buttons",
            className: styles.billingSelectorsWrapper,
            children: [/*#__PURE__*/_jsx(RadioButtonWithLabel, {
              testId: "out-of-scope-radio",
              label: 'Bill an out of scope charge',
              checked: selectedCheckboxOption === BillingOption.outOfScope,
              onChange: () => handleChooseBillingOption(BillingOption.outOfScope)
            }), /*#__PURE__*/_jsx(RadioButtonWithLabel, {
              testId: "in-agreement-radio",
              label: 'Bill a service listed in the agreement',
              checked: selectedCheckboxOption === BillingOption.inAgreement,
              onChange: () => handleChooseBillingOption(BillingOption.inAgreement)
            })]
          }), showServicesSelector && /*#__PURE__*/_jsx(Controller, {
            control: control,
            name: "selectedService",
            rules: requiredValidation(true),
            render: renderProps => /*#__PURE__*/_jsx(Autocomplete, {
              className: styles.manualServicesSelector,
              selectOnFocus: true,
              "data-testid": "agreement-manual-services-selector",
              textFieldProps: {
                placeholder: 'Choose a service to bill',
                error: !!renderProps.fieldState.error
              },
              value: renderProps.field.value,
              getOptionSelected: getServiceOptionSelected,
              getOptionLabel: getServiceOptionLabel,
              options: serviceOptions,
              onBlur: renderProps.field.onBlur,
              noOptionsText: "No manual services available",
              getOptionDisabled: option => option.disabledReason.isDisabled,
              renderOption: option => option.disabledReason.isDisabled ? /*#__PURE__*/_jsx(Tooltip, {
                placement: "right",
                title: option.disabledReason.reasonTooltip,
                children: /*#__PURE__*/_jsxs("div", {
                  className: styles.serviceOptionContent,
                  children: [/*#__PURE__*/_jsx(Typography, {
                    variant: "paragraph1",
                    children: option.name
                  }), /*#__PURE__*/_jsx(Typography, {
                    variant: "paragraph1",
                    skin: "secondary",
                    children: option.costString
                  })]
                })
              }) : /*#__PURE__*/_jsxs("div", {
                className: styles.serviceOptionContent,
                children: [/*#__PURE__*/_jsx(Typography, {
                  variant: "paragraph1",
                  children: option.name
                }), /*#__PURE__*/_jsx(Typography, {
                  variant: "paragraph1",
                  skin: "secondary",
                  children: option.costString
                })]
              }),
              loading: isCommonDataLoading,
              onChange: (event, value) => {
                const service = value ? value : null;
                if (service === selectedService) {
                  return;
                }
                renderProps.field.onChange(service);
              },
              onInputChange: (e, value) => {
                const service = serviceOptions.find(option => option.name === value);
                if (service) {
                  renderProps.field.onChange(service);
                }
              }
            })
          })]
        })]
      })
    })
  });
}